/**
 * `<caos-field-renderer>` — renders ANY typed field by its type + resolved field-level
 * security. (Catalog: Fields & input › FieldRenderer — draws whichever field it is handed, reading or editing, in that field’s own type.)
 *
 * Given a field's metadata (its kernel `storageType` + config), a value, and a `mode`
 * (`read` | `edit`), it renders the correct control by DELEGATING to the AF1/AF2 field
 * primitives — text/number/date/checkbox/picklist — and to `caos-lookup-field` for a
 * reference. It holds NO data of its own and performs no fetch: options for a Select and
 * the result source for a Reference are supplied as properties.
 *
 * The type dispatch is an EXHAUSTIVE `switch` over the kernel's `StorageType` union
 * (imported type-only from `@caos/kernel`, so the contract is the real one but no kernel
 * runtime — and therefore no `zod` — is pulled into this pure leaf library). The default
 * branch narrows to `never`: if the kernel adds a storage kind and a case is not added
 * here, `packages/caos-ui`'s `tsc -p tsconfig.build.json` FAILS to compile. That mirrors
 * the kernel's own exhaustive-switch discipline (`overridableUnderlyingStorage`, the K2
 * three-axis model).
 *
 * Computed / engine-assigned kinds (`Formula`, `Rollup`, `AutoNumber`) are never
 * client-writable (kernel `READ_ONLY_STORAGE_TYPES`), so edit mode renders their
 * read-only display. An `Overridable` field resolves to its computed side's underlying
 * leaf type and renders THAT leaf's editable control (its nullable override column). A
 * `masked` field (FLS/data-classification hidden) renders a masked placeholder in either
 * mode, never the value.
 *
 * Emits a bubbling, composed `caos-field-change` (`{ value }`) when the editable control
 * changes. Styling seams: `::part(value)`, `::part(masked)`, `::part(control)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';
import { deepActiveElement } from '../focus.js';
import './text-field.js';
import './number-field.js';
import './date-field.js';
import type { CaosDateFieldFormat } from './date-field.js';
import './checkbox-field.js';
import './picklist-field.js';
import './tag.js';
import './pill-container.js';
import './lookup-field.js';
import './location-field.js';
import { formatPlace } from '../place-format.js';
import {
  ADDRESS_PARTS,
  ADDRESS_PART_LABELS,
  formatAddress,
  formatAddressLines,
  type AddressPart,
  type AddressValue,
} from '../address-format.js';
import { richTextFragment, linkifyRichText, isRichTextEmpty } from '../rich-text.js';
import { richTextEditor, RICH_TEXT_STYLES, type RichTextToolbarContribution } from '../rich-text-editor.js';
import type { CaosPicklistOption, CaosPicklistPresentation } from './picklist-field.js';
import type { CaosLookupSearch } from './lookup-field.js';
// TYPE-ONLY: the real kernel field contracts. Erased at build — no kernel/zod runtime is
// pulled into this leaf library — while still binding the dispatch to the actual union,
// so a kernel storage-type change is a COMPILE error here (see the `never` default below).
import type { StorageType, Field, FieldFormat } from '@caos/kernel/meta';

/** The rendering-relevant slice of the real kernel `Field` metatype (stays in lock-step with it). */
export type CaosFieldMeta = Pick<
  Field,
  'storageType' | 'format' | 'label' | 'required' | 'scale' | 'formulaReturnType' | 'helpText' | 'length'
>;

/**
 * CAOS-283 — the field's label as a STRING to draw.
 *
 * `CaosFieldMeta` is a `Pick` of the kernel `Field` metatype and stays in lock-step with it by
 * design, so when a label became `string | { default, translations }` this component started
 * receiving both shapes. That is caught here by the compiler rather than at runtime — unlike the
 * kernel's own describe projections, which cast a stored body to `unknown` and so see no change at
 * all — and the thirteen call sites below go through this one function because a renderer that
 * printed `[object Object]` where a field name belongs is the failure being avoided.
 *
 * The DEFAULT is what renders. Choosing the viewer's language needs the viewer's language, which
 * this component is not given and should not guess: a component that reached for an ambient locale
 * would render differently from the surface around it. The resolved string arrives as metadata,
 * the same way every other word on the screen does.
 */
function labelText(label: CaosFieldMeta['label']): string {
  // A STRING, never `undefined`: `label` is required on the Field metatype and a map's `default` is
  // `min(1)`, so a readable string always exists. Widening the return type instead would push an
  // impossible case onto thirteen call sites that rightly expect a name to be there.
  if (typeof label === 'string') return label;
  if (label !== null && typeof label === 'object') {
    const def = (label as { default?: unknown }).default;
    if (typeof def === 'string' && def.length > 0) return def;
  }
  return '';
}

/**
 * `read` — static value (with the mono kicker label above it, the Dragon record-detail
 * key-value pair). `edit` — the boxed form control (create/edit forms). `inline` — the
 * P5b record-detail affordance: the value renders in place with a dashed-underline hover
 * cue and, on click, converts IN PLACE to the type-matched underline editor (commit on
 * blur/Enter, cancel on Escape). FLS-masked and computed/read-only fields never offer edit.
 */
export type CaosFieldMode = 'read' | 'edit' | 'inline';

const sheet = makeSheet(`
  :host { display: block; }

  .field-read { display: block; }

  /* Read-mode label = mono kicker (Dragon objectFramework kicker: 10px .22em uppercase muted). */
  .kicker {
    display: block;
    font-family: var(--caos-font-mono);
    font-size: 10px;
    font-weight: var(--caos-font-weight-label);
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
    line-height: 1.4;
    margin-bottom: var(--caos-space-1);
  }

  .value {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.5;
    color: var(--caos-color-text);
    display: inline-block;
    min-height: 20px;
  }
  :host([numeric]) .value { font-variant-numeric: tabular-nums; }

  /* CAOS-600 - a value that IS a way of reaching something reads as a link, in the same accent the
     linked-record reference uses, so one kind of "this goes somewhere" looks like every other. */
  a.value.link {
    color: var(--caos-color-accent);
    text-decoration: none;
  }
  a.value.link:hover,
  a.value.link:focus-visible {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .value.blank { color: var(--caos-color-text-muted); }

  /* CAOS-410 — the field's own help text, under the control it explains. Muted and a size
     below body so it reads as guidance rather than as another value; \`white-space: pre-line\`
     so an author who wrote two lines gets two lines. Never tabular, even on a Number field:
     it is prose, and \`:host([numeric])\` must not reach it. */
  .help {
    font-family: var(--caos-font);
    font-size: 11.5px;
    line-height: 1.45;
    color: var(--caos-color-text-muted);
    margin-top: var(--caos-space-1);
    white-space: pre-line;
    font-variant-numeric: normal;
  }

  /* ── P5b inline record-detail affordance ── */
  .inline-host { display: inline-block; max-width: 100%; }
  .inline-read {
    display: inline-block;
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.5;
    color: var(--caos-color-text);
    cursor: text;
    outline: none;
    border-bottom: 1px dashed transparent;
    transition: border-color var(--caos-duration-fast, 120ms) ease;
  }
  .inline-read:hover,
  .inline-read:focus-visible { border-bottom-color: var(--caos-color-accent); }
  .inline-read.blank { color: var(--caos-color-text-muted); font-style: italic; }
  /* A Reference value reads as a hyperlink (Dragon lookup-link): accent, bold, and NO
     underline at rest; on hover a clean text underline (offset 2px) replaces the dashed
     edit cue, so the link affordance stays distinct from the inline-edit hint. */
  .inline-read.ref { color: var(--caos-color-accent); font-weight: 700; }
  .inline-read.ref:hover,
  .inline-read.ref:focus-visible {
    border-bottom-color: transparent;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  :host([numeric]) .inline-read { font-variant-numeric: tabular-nums; }

  .masked {
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-2);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    color: var(--caos-color-text-muted);
    letter-spacing: 0.06em; /* masked-value spacing — off-scale tracking, stays literal */
  }
  .masked .lock { width: 14px; height: 14px; }
  .masked .lock svg { display: block; width: 100%; height: 100%; }

  .readonly {
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-2);
  }
  .readonly .lock { width: 14px; height: 14px; color: var(--caos-color-text-muted); }
  .readonly .lock svg { display: block; width: 100%; height: 100%; }

  /* CAOS-595 — the rich format: a block of formatted text, read and edited with the same styles. */
  .value.rich, .inline-read.rich { display: block; }

  /* CAOS-1674 — the address format. Read: the lines of an envelope, so each part sits under the
     last rather than running together as one sentence. Edit: the same stack, one box per part.
     Both use the platform spacing scale rather than a number chosen here. */
  .value.address { display: block; }
  .address-line { display: block; }
  .address { display: flex; flex-direction: column; gap: var(--caos-space-2); }
${RICH_TEXT_STYLES}
`);

/**
 * CAOS-600 — THE ONE NAMED DISPLAY EACH TEMPORAL KIND READS IN.
 *
 * The incumbent catalogue ships a formatted-date-time component with thirteen attributes, and those
 * thirteen are a re-export of one browser formatting API: every option passed through, so every page
 * gets to invent its own date. A value's format is a property of what it IS, not of who is drawing
 * it, so the choice is made HERE, once, and the same stored instant reads the same way on a record
 * page, in a list view and in a document.
 *
 * A day reads as a day. An instant reads as a day and a time. A time of day reads as a time and
 * nothing else — a clock time is not on a date, and putting today's date beside it would be
 * inventing information the column does not hold.
 *
 * The reader's own locale orders and names the parts; what is fixed here is WHICH parts, not their
 * order, because a month before a day is a locale's business and not this file's.
 */
export const TEMPORAL_DISPLAY = {
  // A DAY, like a time of day, is read back in UTC. `2026-03-14` denotes a calendar day and carries
  // no zone; parsed as UTC midnight and formatted in a zone behind UTC it renders as the 13th, which
  // is the same stored value reading as a different day depending on who looks at it. Only `DateTime`
  // is an instant, and only an instant is formatted in the reader's own zone.
  Date: { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' },
  DateTime: { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
  // `ZonedDateTime` is deliberately ABSENT, and the reason is the same one that keeps it out of the
  // `relative` format. It is a wall-clock time in a NAMED ZONE, stored as two columns, and the zone is
  // half the value. Formatting it here — in whatever zone the reader happens to be in — would show two
  // readers two different clock times for one appointment and look completely correct to both. Reading
  // it needs the zone it declares, which is a question this ticket does not answer, so it keeps the
  // behaviour it had: the stored value, shown as it is.
  // A zoneless time of day is read back in UTC ON PURPOSE. `Time` stores a wall-clock time with
  // no zone, and the reference day it is parsed against is arbitrary; formatting it in the reader's
  // zone would shift 14:30 to 16:30 for a reader two hours east, which is not a different rendering
  // of the value but a different value.
  Time: { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' },
} as const satisfies Partial<Record<StorageType, Intl.DateTimeFormatOptions>>;

/** The storage kinds read as a moment rather than as a string. */
export const TEMPORAL_STORAGE_TYPES = Object.keys(TEMPORAL_DISPLAY) as readonly StorageType[];

/**
 * An editor whose `change` event fires once PER SEGMENT rather than once when the person has
 * finished — a native `date`, `time` or `datetime-local` input, which is one box holding several
 * fields (hour and minute; month, day and year).
 *
 * CAOS-1678. The platform fires `change` on these as soon as the value is COMPLETE, and on a
 * pre-filled value that is after the FIRST segment is touched. Measured in Chromium rather than
 * reasoned about: a focused `<input type="time" value="09:30">` given a single ArrowUp fires one
 * `change` carrying `10:30` — the minute never reached. An EMPTY field does not behave this way,
 * because the first segment leaves the value incomplete, which is why this only ever bit a field
 * that already had a value.
 *
 * So `change` cannot stand in for "finished" here, the way it can for a text box that fires it on
 * blur. The control that has this property says so, and {@link CaosFieldRenderer.inlineValue} asks
 * the EDITOR rather than testing the storage kind — because an `Overridable` whose computed side is
 * a date renders this same control while its own `storageType` is `Overridable`. Keying the two
 * halves of the policy on two different facts is how one of them fires without the other.
 *
 * Named after what is observable, in the same spirit as `holdsRefusal` on the location field.
 */
type SegmentedEditor = HTMLElement & { firesChangePerSegment?: boolean };

/** Whether this editor's `change` is a per-segment event rather than a finish signal. */
function firesChangePerSegment(editor: HTMLElement): boolean {
  return (editor as SegmentedEditor).firesChangePerSegment === true;
}

/**
 * The units "3 days ago" can be counted in, largest first, with how many seconds each one is.
 *
 * Largest-first is the whole algorithm: the first unit the elapsed time fills is the one it is
 * reported in, so a fortnight is two weeks rather than fourteen days and an hour is an hour rather
 * than sixty minutes. Anything under a minute has no unit and reads as "now" — counting seconds
 * would produce a number that is wrong by the time it is read.
 */
const RELATIVE_UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3600],
  ['minute', 60],
];

/**
 * CAOS-600 — a moment read as "3 days ago".
 *
 * A DATE is counted in whole calendar days, not in elapsed time, because a date has no time of day:
 * yesterday's date is "yesterday" whether it is read at one minute past midnight or at midnight
 * less one minute. Counting elapsed milliseconds from an assumed midnight would make the same
 * stored date read as "yesterday" in the morning and "2 days ago" in the evening.
 *
 * An INSTANT is counted in elapsed time, because that is what it measures.
 */
export function relativeMoment(value: Date, storageType: StorageType, now: Date = new Date()): string {
  const fmt = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (storageType === 'Date') {
    const day = (d: Date): number => Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86_400_000);
    return fmt.format(day(value) - day(now), 'day');
  }
  const seconds = (value.getTime() - now.getTime()) / 1000;
  for (let i = 0; i < RELATIVE_UNITS.length; i += 1) {
    const [unit, size] = RELATIVE_UNITS[i]!;
    if (Math.abs(seconds) < size) continue;
    const rounded = Math.round(seconds / size);
    // The unit is the largest the elapsed time FILLS — but rounding inside it can reach its own
    // ceiling, and then the unit above is the right one: 59m59s fills only minutes and rounds to 60 of
    // them, which is "an hour", and 23h59m59s rounds to 24 hours, which is "yesterday". Promoted only
    // when the rounding actually reaches the larger unit, so 12h01m stays twelve hours rather than
    // being rounded up into a day — a promotion on anything past half a unit loses more than it fixes.
    const larger = RELATIVE_UNITS[i - 1];
    if (larger && Math.abs(rounded) * size >= larger[1]) return fmt.format(Math.round(seconds / larger[1]), larger[0]);
    return fmt.format(rounded, unit);
  }
  // Under a minute either way: the smallest unit above did not fill, so nothing did. `numeric: 'auto'`
  // renders a zero-second difference as "now", which is the right answer — a count of seconds is wrong
  // by the time it is read. This is where that decision lives; guarding for it before the loop as well
  // looked careful and was untestable, because the loop already declines every unit.
  return fmt.format(0, 'second');
}

/**
 * The moment a stored temporal value denotes, or `null` when it does not denote one.
 *
 * A `Time` arrives as a wall-clock string with no date (`14:30`, `14:30:00`), which `new Date(...)`
 * cannot read on its own — it is given an arbitrary reference day so the formatter has a moment to
 * format, and only the time parts of it are ever drawn.
 */
export function temporalMoment(storageType: StorageType, value: unknown): Date | null {
  if (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date)) return null;
  const raw = value instanceof Date ? value : String(value).trim();
  if (raw === '') return null;
  const moment =
    typeof raw === 'string' && storageType === 'Time' ? new Date(`1970-01-01T${raw}${/[zZ+]|-\d\d:?\d\d$/.test(raw) ? '' : 'Z'}`) : new Date(raw as string | number);
  return Number.isNaN(moment.getTime()) ? null : moment;
}

/**
 * CAOS-600 — the Text formats whose value IS a way of reaching something, and the URL each becomes.
 *
 * `email` and `phone` BUILD their URL, so the scheme is this file's and the stored value is only ever
 * the part after it. `url` is the dangerous one, because the stored value is the whole URL: a field
 * holding `javascript:alert(document.cookie)` would otherwise be rendered as a real anchor that runs
 * it, and the read view deliberately lets a click on an anchor through rather than turning it into an
 * edit — so nothing downstream would stop it. Anything that is not plainly the web is refused here and
 * read as text instead.
 *
 * ALLOW-LISTED, not blocked. A deny-list of `javascript:` and `data:` is a list somebody has to keep
 * complete forever, and the interesting schemes are the ones nobody thought of. `http` and `https`
 * are what a URL field is for; a relative path is allowed because it cannot carry a scheme at all.
 *
 * `email` is allow-listed too, for a quieter reason: BUILDING the scheme is not the same as owning
 * the URL. Everything after a `?` in a `mailto:` is headers, so a stored value can carry the message
 * as well as the recipient, and a comma adds recipients. One address, or it reads as text.
 */
const LINKED_TEXT_FORMATS = {
  email: (v: string) => (isMailAddress(v) ? `mailto:${v}` : null),
  phone: (v: string) => `tel:${v.replace(/[^+0-9]/g, '')}`,
  url: (v: string) => (isWebUrl(v) ? v : null),
} as const;

/**
 * Whether a stored string is ONE mail address, rather than a mail somebody else composed.
 *
 * A `mailto:` URL is not a recipient with a scheme in front of it. Everything after a `?` is
 * HEADERS, and a comma separates recipients, so `someone@example.com?bcc=everyone@example.com&
 * subject=Wire%20transfer&body=...` is a single stored string that opens a pre-addressed,
 * pre-written message from the reader's own mail client. Nothing about it looks wrong to the reader
 * until it is sent. Stripping the headers would still leave a link the value did not ask for, so the
 * value is refused whole and read as text, the same answer a `url` gets.
 *
 * Not a validity check. The kernel's `email` format owns whether the stored value is a real address;
 * this owns whether it is ONE address and nothing more. A value in the display-name form somebody
 * pastes out of a mail client — `Jane Doe <jane@example.com>` — reads as text rather than being
 * picked apart here, because guessing which half of it is the address is inventing information.
 */
function isMailAddress(value: string): boolean {
  // The characters that change what the URL MEANS rather than who it reaches. `?` opens the headers
  // and `,` and `;` add recipients; `:` would let the value name a scheme of its own, so a stored
  // `mailto:dana@example.com` cannot become `mailto:mailto:...`; any space is not in an address.
  //
  // `%` is refused for a reason worth writing down, because it is the one that is not obvious. A
  // percent escape is not decoded HERE, and RFC 6068 says a header list starts at a LITERAL `?`, so
  // `dana@example.com%3Fbcc=everyone@example.invalid` should reach a conformant client as one long
  // address and do nothing. Should. The thing that decodes it is whatever mail application the
  // reader's machine has registered, which this file cannot see, cannot version and cannot test
  // against — and the two orders it might decode and parse in give opposite answers. Decoding here
  // instead would need this file to be right about every client's order too. So a value carrying a
  // percent escape reads as text, and the cost is an address with a `%` in its local part — legal,
  // and something almost nobody has — reading as the text it is, which is the same answer every
  // other value this function cannot vouch for gets. The colon costs one more thing worth saying out
  // loud beside it: an address whose domain is written as an IPv6 literal, `user@[IPv6:2001:db8::1]`,
  // reads as text too. Legal, and in the same rarity class as the percent.
  if (/[?,;:%\s]/.test(value)) return false;
  return /^[^@\s]+@[^@\s]+$/.test(value);
}

/**
 * Whether a stored string is a URL the workspace will link to: the web, or a path within it.
 *
 * Parsed rather than pattern-matched, so the decision is made on the scheme the BROWSER will resolve
 * rather than on how the text looks. `java\nscript:alert(1)` and `JavaScript:alert(1)` both read as
 * that scheme once parsed, and both are refused here; a pattern over the raw text has to anticipate
 * every way of writing it.
 */
function isWebUrl(value: string): boolean {
  // A relative path carries no scheme, so there is nothing for it to smuggle. Resolved against a
  // fixed base purely to make it parseable — the base is never used for anything else.
  try {
    const parsed = new URL(value, 'https://caos.invalid/');
    if (!/^https?:$/.test(parsed.protocol)) return false;
    // A URL may carry a user before its host, and the browser resolves the part AFTER the `@`:
    // `https://accounts.example.com@evil.invalid/` is a link to `evil.invalid` that reads as one to
    // `accounts.example.com`, and the reader is shown the stored text. Refused, because a link whose
    // visible text names a different host than the one it opens is the whole of that trick. Nothing
    // an ordinary workspace URL needs is lost: credentials in a URL are not a thing to store in a
    // field either way.
    if (parsed.username !== '' || parsed.password !== '') return false;
    // An absolute value must have SAID it was http(s); one that resolved to the placeholder base was
    // relative, which is allowed. Either way the protocol above is what the browser will use.
    return true;
  } catch {
    return false;
  }
}

/** CAOS-595 — a Text field in the rich format, which the renderer reads and edits as formatted text. */
function isRich(meta: CaosFieldMeta): boolean {
  return meta.storageType === 'Text' && meta.format === 'rich';
}

/** CAOS-600 — a field read as "3 days ago" rather than as a calendar date. */
function isRelative(meta: CaosFieldMeta): boolean {
  return meta.format === 'relative';
}

/** Compile-time exhaustiveness guard: reaching this means a `StorageType` case is missing. */
function assertNever(x: never): never {
  throw new Error(`caos-field-renderer: unhandled storage type ${String(x)}`);
}

/**
 * CAOS-452 — what a Text field in a given format is EDITED as, decided exhaustively.
 *
 * This is the acceptance criterion that outlives the three affordances it was written for: the
 * renderer must read the format from the declaration rather than carry its own list, so a format
 * added to the model cannot be silently unhandled. A lookup table cannot do that — a format it has
 * never heard of falls through it and draws the default box, which is precisely the failure this
 * ticket is about, one layer up.
 *
 * So it is a SWITCH over the kernel's own `FieldFormat`, ending in the same `assertNever` the
 * storage axis already uses. Add a format to `FIELD_FORMATS` and this stops compiling until
 * somebody says what a Text field does with it. The answer is allowed to be "nothing special" —
 * `relative`, `currency` and `percent` all give that answer, because they are formats of other
 * storage kinds and a Text field carrying one is a pairing the kernel refuses anyway. What is not
 * allowed is for the question to go unasked.
 *
 * Returns the string `<caos-text-field>` names its presentations by, not an input `type`: which
 * type each needs is that component's business, and `long` has no type at all because it is drawn
 * as a text area.
 */
function textFieldFormat(format: FieldFormat | undefined): string {
  if (format === undefined) return '';
  switch (format) {
    case 'email':
    case 'phone':
    case 'url':
    case 'long':
      return format;
    // Drawn by the rich editor, which is chosen before this is reached.
    case 'rich':
      return '';
    // Formats of a different storage kind. `FORMAT_STORAGE` pairs each of these with Number, Date
    // or DateTime, and the pairing is enforced on the parse path AND by the authoritative
    // validator, so a Text field declaring one cannot be deployed. Named anyway: the point of the
    // exhaustive switch is that every format is answered here, including the ones whose answer is
    // that they never arrive.
    case 'currency':
    case 'percent':
    case 'relative':
    // CAOS-1674 — `address` is the format on JSON, so it reaches a text field even less than the
    // others: not merely a different scalar kind but a different SHAPE. Named here for the same
    // reason as the three above — the point of the exhaustive switch is that every format is
    // answered, including the ones whose answer is that they never arrive.
    case 'address':
      return '';
    default:
      return assertNever(format);
  }
}

export class CaosFieldRenderer extends CaosElement {
  static get observedAttributes(): string[] {
    return ['mode', 'masked', 'required'];
  }

  private body: HTMLDivElement;
  private _field: CaosFieldMeta | null = null;
  private _value: unknown = null;
  private _options: CaosPicklistOption[] = [];
  /** CAOS-640 — the value set's declared presentation. Absent ⇒ the dropdown this always drew. */
  private _optionsPresentation: CaosPicklistPresentation = 'dropdown';
  private _search: CaosLookupSearch | null = null;
  private _refDisplay: string | null = null;
  private _refHref: string | null = null;
  private _richTextToolbar: RichTextToolbarContribution[] = [];

  constructor() {
    super(sheet);
    this.body = document.createElement('div');
    this.body.setAttribute('part', 'control');
    this.root.append(this.body);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this.render();
  }

  /** The field metadata (its kernel storage type + config). */
  get field(): CaosFieldMeta | null {
    return this._field;
  }
  set field(meta: CaosFieldMeta | null) {
    this._field = meta;
    this.render();
  }

  /** The field's value. */
  get value(): unknown {
    return this._value;
  }
  set value(v: unknown) {
    this._value = v;
    this.render();
  }

  get mode(): CaosFieldMode {
    const m = this.getAttribute('mode');
    return m === 'edit' || m === 'inline' ? m : 'read';
  }
  set mode(m: CaosFieldMode) {
    this.setAttribute('mode', m);
  }

  get masked(): boolean {
    return this.hasAttribute('masked');
  }
  set masked(v: boolean) {
    if (v) this.setAttribute('masked', '');
    else this.removeAttribute('masked');
  }

  /** Options for a Select / MultiSelect field (data in — never queried at this layer). */
  get options(): CaosPicklistOption[] {
    return this._options;
  }
  set options(list: CaosPicklistOption[]) {
    this._options = list ?? [];
    this.render();
  }

  /**
   * CAOS-640 — HOW those options are presented, as the value set declares it. It rides beside
   * `options` rather than inside {@link CaosFieldMeta} because it is a property of the VALUE SET,
   * not of the field: two fields drawing from the same set present the same way. This layer only
   * forwards it — the choice was made in metadata, not by anyone inspecting the field here.
   */
  get optionsPresentation(): CaosPicklistPresentation {
    return this._optionsPresentation;
  }
  set optionsPresentation(p: CaosPicklistPresentation) {
    this._optionsPresentation = p === 'cards' || p === 'radio' ? p : 'dropdown';
    this.render();
  }

  /** The injected result source for a Reference field's picker (access-scoped by the app). */
  get search(): CaosLookupSearch | null {
    return this._search;
  }
  set search(fn: CaosLookupSearch | null) {
    this._search = fn;
    this.render();
  }

  /** The linked-record display label for a Reference field (read mode). */
  get refDisplay(): string | null {
    return this._refDisplay;
  }
  set refDisplay(v: string | null) {
    this._refDisplay = v;
    this.render();
  }

  /** The linked-record href for a Reference field (read mode). */
  get refHref(): string | null {
    return this._refHref;
  }
  set refHref(v: string | null) {
    this._refHref = v;
    this.render();
  }

  /**
   * CAOS-595 — the buttons packages add to a rich text field's toolbar, read off their `rich-text`
   * capability claims by whoever mounts the renderer. Only a Text field in the `rich` format draws them.
   */
  get richTextToolbar(): RichTextToolbarContribution[] {
    return this._richTextToolbar;
  }
  set richTextToolbar(list: RichTextToolbarContribution[]) {
    this._richTextToolbar = Array.isArray(list) ? list : [];
    this.render();
  }

  private render(): void {
    this.body.replaceChildren();
    const meta = this._field;
    if (!meta) return;

    // numeric alignment marker
    if (meta.storageType === 'Number' || meta.storageType === 'AutoNumber') this.setAttribute('numeric', '');
    else this.removeAttribute('numeric');

    const mode = this.mode;

    // Edit (form) mode → the boxed control, no kicker (the control carries its own label).
    if (mode === 'edit' && !this.masked) {
      this.body.append(this.editControl(meta.storageType, meta));
      // CAOS-410 — the field's help text, under the control. AFTER it rather than above, because
      // it explains what to type: read the label, look at the box, then read the hint. A masked
      // field gets none — the help would describe a value this person is not being shown.
      const help = this.helpFor(meta);
      if (help) this.body.append(help);
      return;
    }

    // Read / inline → the Dragon key-value pair: mono kicker label above the value.
    const wrap = document.createElement('div');
    wrap.className = 'field-read';
    if (labelText(meta.label)) wrap.append(this.kicker(labelText(meta.label)));
    if (this.masked) {
      wrap.append(this.maskedDisplay());
    } else if (mode === 'inline' && this.isInlineEditable(meta.storageType)) {
      wrap.append(this.inlineValue(meta));
    } else {
      wrap.append(this.readDisplay(meta));
    }
    this.body.append(wrap);
  }

  /**
   * CAOS-410 — the field's authored help text as an element, or `null` when it has none.
   *
   * Blank-safe on purpose: the kernel schema requires a non-empty string, but this layer is handed
   * a plain object by whoever mounts it and an empty (or whitespace) value must render nothing at
   * all rather than an empty muted line that pushes the next field down for no reason.
   */
  private helpFor(meta: CaosFieldMeta): HTMLElement | null {
    const text = typeof meta.helpText === 'string' ? meta.helpText.trim() : '';
    if (text === '') return null;
    const el = document.createElement('div');
    el.className = 'help';
    el.setAttribute('part', 'help');
    el.textContent = text;
    return el;
  }

  /** The mono uppercase kicker rendered above a field's value (Dragon record-detail label). */
  private kicker(label: string): HTMLElement {
    const el = document.createElement('span');
    el.className = 'kicker';
    el.setAttribute('part', 'label');
    el.textContent = label;
    return el;
  }

  /**
   * Whether a storage type offers the P5b inline click-to-edit affordance. Computed /
   * engine-assigned kinds and types with no editor primitive stay static (no affordance).
   *
   * That is the whole rule, and it is the rule a reader should be able to trust: a kind belongs on
   * the list below only while it has no editor. File is the single exception and says why in place.
   * Anything else here with an editor is an oversight rather than a decision, because nothing
   * distinguishes the two from the outside — CAOS-1673 was exactly that, for Time.
   */
  private isInlineEditable(storageType: StorageType): boolean {
    switch (storageType) {
      case 'Formula':
      case 'Rollup':
      case 'AutoNumber':
      // CAOS-1673 — Time is deliberately NOT on this list. It was, from before it had an editor,
      // and CAOS-600 gave it one without revisiting here: a time of day was editable in a form and
      // frozen on the record page, while Date and DateTime — the same date-field control, one input
      // type apart — were editable in both. The list means what its doc above says, so a kind
      // belongs on it only while it has no editor, or while it has a reason written down the way
      // File does below.
      case 'ZonedDateTime':
      case 'JSON':
      // CAOS-596 — File is the one kind on this list that now HAS an editor and stays off it
      // anyway, so the reason is worth stating rather than leaving it to read as an oversight.
      // The inline affordance commits on blur. Opening a file picker blurs the document, so an
      // inline file editor would commit-and-close the moment it was used, every time. It is also
      // not a value swap: attaching is a multi-file, asynchronous act with its own per-file
      // progress and failure, which is a panel's worth of interface inside a one-line cue.
      // Edit mode gets the control; the record page keeps the read rendering.
      case 'File':
      case 'Localizable':
        return false;
      default:
        return true;
    }
  }

  // ── P5b inline record-detail editing ────────────────────────────────────────

  /**
   * The inline value: renders the value in place with a dashed-underline hover cue and,
   * on click, swaps to the type-matched underline editor. Commit on blur/Enter (the sub-
   * field's own `change`, or the lookup's `caos-lookup-change`), cancel on Escape. The
   * commit re-emits the existing `caos-field-change`. A Boolean renders its box directly
   * (the box is its own affordance); everything else is click-to-edit.
   */
  private inlineValue(meta: CaosFieldMeta): HTMLElement {
    const host = document.createElement('span');
    host.className = 'inline-host';

    // Boolean: the checkbox IS the affordance — render it live, toggle commits.
    if (meta.storageType === 'Boolean') {
      host.append(this.inlineBooleanEditor(meta));
      return host;
    }

    let editing = false;

    // Containment across shadow roots. The focused leaf is usually inside the editor's OWN shadow
    // root, which `host.contains` cannot see into, so the check walks out through each shadow host.
    // Without that, an editor with more than one box (the location editor, CAOS-849) reads focus
    // moving from one of its boxes to the other as focus leaving, and closes under the person typing.
    const hostHasFocus = (): boolean => {
      for (let node: Node | null = deepActiveElement(); node; ) {
        if (node === host || host.contains(node)) return true;
        const root = node.getRootNode();
        node = root instanceof ShadowRoot ? root.host : null;
      }
      return false;
    };

    const commitAndClose = (): void => {
      editing = false;
      renderRead();
    };

    const startEdit = (): void => {
      if (editing) return;
      editing = true;
      host.replaceChildren();
      const editor = this.editControl(meta.storageType, meta);
      editor.setAttribute('inline', '');
      // What a segmented editor opened holding, so blur can tell a real edit from a look. Assigned
      // below, once the editor is in the DOM; every reader of it runs on a later turn.
      let openedWith: unknown;
      if (meta.storageType === 'Reference') {
        editor.addEventListener('caos-lookup-change', (e) => {
          const detail = (e as CustomEvent<{ value: string; label: string }>).detail;
          if (detail) this._refDisplay = detail.label;
          commitAndClose();
        });
        editor.addEventListener('caos-lookup-clear', () => {
          this._refDisplay = null;
          commitAndClose();
        });
      } else if (!firesChangePerSegment(editor)) {
        // A text or number box fires `change` on blur, so this IS the commit-on-blur this method's
        // own contract promises. A segmented editor fires it mid-edit instead, and is committed by
        // the `focusout` path below — which is the same moment, reached by a signal that means it.
        editor.addEventListener('change', () => commitAndClose());
      }
      editor.addEventListener('keydown', (e) => {
        const key = (e as KeyboardEvent).key;
        if (key === 'Escape') {
          // cancel — restore the read view without emitting a change
          e.stopPropagation();
          commitAndClose();
        } else if (key === 'Enter' && meta.storageType !== 'Reference' && !isRich(meta)) {
          // commit on Enter (a formless input fires native `change` only on blur, so
          // commit explicitly — mirrors CrmField's Enter-to-save).
          e.preventDefault();
          const v = (editor as unknown as { value?: unknown }).value;
          this.dispatchChange(v);
          commitAndClose();
        }
      });
      editor.addEventListener('focusout', () => {
        setTimeout(() => {
          // An editor holding a refused value stays open when focus leaves it (CAOS-849). Closing would
          // put the stored value back and drop what the person typed, and the refusal with it, with
          // nothing on screen to say so. Escape is still the way to discard.
          if (editing && !hostHasFocus() && (editor as HTMLElement & { holdsRefusal?: boolean }).holdsRefusal !== true) {
            // CAOS-1678 — a segmented editor has been silent until now, so blur is where its value is
            // saved. Compared against what it OPENED holding so that opening a field and clicking
            // away writes nothing: `change` gave the other kinds that for free, because the platform
            // does not fire it when a value is untouched. `openedWith` is read AFTER the control is
            // in the DOM, so it is the value the input actually holds once the platform has had its
            // say — a `time` input rejects `09:30Z` outright, and comparing against the stored
            // string instead would read that rejection as an edit and clear the field on a mere look.
            if (firesChangePerSegment(editor)) {
              const now = (editor as unknown as { value?: unknown }).value;
              if (String(now ?? '') !== String(openedWith ?? '')) this.dispatchChange(now);
            }
            commitAndClose();
          }
        }, 0);
      });
      host.append(editor);
      openedWith = (editor as unknown as { value?: unknown }).value;
      // CAOS-410 — the same help text the form shows, shown here too, but only while the editor is
      // OPEN. On a record page the field is a value most of the time and guidance beside a value is
      // noise; the moment somebody is typing into it, it is the same question a form asks. It goes
      // away with the editor: `renderRead` replaces the host's children.
      const help = this.helpFor(meta);
      if (help) host.append(help);
      queueMicrotask(() => (editor as unknown as { focus?: () => void }).focus?.());
    };

    const renderRead = (): void => {
      editing = false;
      host.replaceChildren();
      const view = document.createElement('span');
      view.className = 'inline-read';
      view.setAttribute('part', 'value');
      view.setAttribute('role', 'button');
      view.tabIndex = 0;
      view.setAttribute('aria-label', `Edit ${labelText(meta.label) ?? 'field'}`);

      if (meta.storageType === 'Reference') {
        const name = this._refDisplay ?? '';
        if (name) {
          view.classList.add('ref');
          view.textContent = name;
        } else {
          view.classList.add('blank');
          view.textContent = '—';
        }
      } else if (meta.storageType === 'MultiSelect') {
        const arr = Array.isArray(this._value) ? this._value : [];
        if (arr.length === 0) {
          view.classList.add('blank');
          view.textContent = '—';
        } else {
          view.append(this.pillsFor(arr, meta));
        }
      } else if (isRich(meta)) {
        if (isRichTextEmpty(this._value)) {
          view.classList.add('blank');
          view.textContent = '—';
        } else {
          view.classList.add('rich');
          view.append(richTextFragment(this._value));
        }
      } else {
        // CAOS-600 — the same linked read the static display uses. The click handler below already
        // follows a link rather than starting an edit, which is what CAOS-595 taught it for the
        // links inside a rich value, so a mail address here behaves the way one there does.
        const linked = this.linkedRead(meta, this._value);
        if (linked) {
          view.append(linked);
        } else {
          const text = this.formatRead(meta, this._value);
          if (text === '') {
            view.classList.add('blank');
            view.textContent = '—';
          } else {
            view.textContent = text;
          }
        }
      }

      const start = (e: Event): void => {
        // A link inside a rich value is followed, not turned into an edit.
        if (e.type === 'click' && (e.target as Element | null)?.closest?.('a')) return;
        e.stopPropagation();
        startEdit();
      };
      view.addEventListener('click', start);
      view.addEventListener('keydown', (e) => {
        const k = (e as KeyboardEvent).key;
        if (k === 'Enter' || k === ' ') {
          e.preventDefault();
          start(e);
        }
      });
      host.append(view);
    };

    renderRead();
    return host;
  }

  /** A Boolean rendered as a live inline checkbox — toggling commits immediately. */
  private inlineBooleanEditor(meta: CaosFieldMeta): HTMLElement {
    const el = document.createElement('caos-checkbox-field') as HTMLElement & { checked: boolean };
    el.setAttribute('part', 'control');
    el.setAttribute('inline', '');
    if (this._value) el.setAttribute('checked', '');
    if (labelText(meta.label)) el.setAttribute('aria-label', labelText(meta.label));
    el.addEventListener('change', () => this.dispatchChange(el.checked));
    return el;
  }

  // ── read-mode display ──────────────────────────────────────────────────────

  private readDisplay(meta: CaosFieldMeta): HTMLElement {
    // Reference reads as the linked-record link (delegated to the lookup field).
    if (meta.storageType === 'Reference') {
      return this.referenceControl(meta, 'read');
    }
    // MultiSelect reads as its values' tags, held in a pill container (CAOS-597).
    if (meta.storageType === 'MultiSelect') {
      const arr = Array.isArray(this._value) ? this._value : [];
      if (arr.length === 0) return this.blankValue();
      const pills = this.pillsFor(arr, meta);
      pills.setAttribute('part', 'value');
      return pills;
    }
    if (isRich(meta)) return this.richReadDisplay();
    // CAOS-1674 — an address reads on its own lines, like an envelope, so it is a block rather than
    // the single span the plain path builds below. `formatRead` still renders it on ONE line, with
    // the same words, for every caller that needs a string — a list-view cell, a lookup's second
    // line — which is why both exist rather than one being the other's special case.
    if (meta.storageType === 'JSON' && meta.format === 'address') return this.addressReadDisplay();
    // CAOS-600 — before the plain value, because a linked format's value IS the link.
    const linked = this.linkedRead(meta, this._value);
    if (linked) return linked;
    const text = this.formatRead(meta, this._value);
    if (text === '') return this.blankValue();
    const span = document.createElement('span');
    span.className = 'value';
    span.setAttribute('part', 'value');
    span.textContent = text;
    return span;
  }

  /**
   * CAOS-600 — an email, a phone number or a URL read as the thing it reaches.
   *
   * The incumbent catalogue ships three components for this, one per scheme, each a link with the
   * value inside it. They are one thing: a Text field whose declared format says its value is a way
   * of reaching something. The format is already on the field, so nothing new is declared — what
   * changes is that the renderer stops drawing an address as inert text.
   *
   * Returns `null` for a field that is not in one of those formats, so the caller falls through to
   * the plain value it always drew. The value is the link's TEXT unchanged — a phone number is dialled
   * with its punctuation stripped but still reads the way it was typed, because the digits are for
   * the dialler and the spacing is for the person.
   */
  private linkedRead(meta: CaosFieldMeta, value: unknown): HTMLAnchorElement | null {
    if (meta.storageType !== 'Text') return null;
    const scheme = LINKED_TEXT_FORMATS[meta.format as keyof typeof LINKED_TEXT_FORMATS];
    if (!scheme) return null;
    // CAOS-1705 — EMPTY IS DECIDED THE SAME WAY `formatRead` DECIDES IT, and this line is why the
    // ticket exists. It used to read `String(value).trim()`, and `String(null)` is the four-letter
    // string `"null"` — which is not `''`, so an empty field walked straight past the guard below
    // and was rendered as a link whose text was the word `null`. A person reads that as data: a
    // field whose contents are the word null, which is worse than a blank, because blank explains
    // itself and this looks like something went in wrong.
    //
    // EMAIL IS WHY THIS SURVIVED, and it is the detail that makes the bug worth a comment rather
    // than a one-line patch. `email` only escaped because `isMailAddress('null')` is false for want
    // of an `@`, so it returned no href and fell through to the placeholder by accident. `phone`
    // built `tel:` out of a value with every non-digit stripped, and `url` resolved `null` as a
    // RELATIVE path, which `isWebUrl` allows by design. So two of the three formats drew a live
    // anchor over the word `null` while the third looked correct for a reason that had nothing to
    // do with being empty — which is exactly why the record page appeared to handle placeholders
    // properly everywhere except these two fields.
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    if (text === '') return null;
    const href = scheme(text);
    // A value the format cannot safely reach is read as TEXT, not as a dead link and not as nothing:
    // the reader still sees exactly what the column holds, which is the only honest thing to show.
    if (href === null) return null;
    const a = document.createElement('a');
    a.className = 'value link';
    a.setAttribute('part', 'value');
    a.href = href;
    a.textContent = text;
    // A URL is somewhere else; a mail or dial is handled by whatever the reader has set up for it,
    // and opening those in a new tab leaves an empty one behind.
    if (meta.format === 'url') {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    }
    return a;
  }

  private blankValue(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'value blank';
    span.setAttribute('part', 'value');
    span.textContent = '—';
    return span;
  }

  private optionLabel(value: string): string {
    return this._options.find((o) => o.value === value)?.label ?? value;
  }

  /** Tolerant, presentational read formatting. The app's shared formatters win in-app. */
  private formatRead(meta: CaosFieldMeta, value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    // CAOS-600 — a moment reads as a moment. Before this, every temporal kind fell through to the
    // default below and a record page showed the stored string: `2026-03-14T09:30:00.000Z` where a
    // date belonged. The formats are named in `TEMPORAL_DISPLAY` rather than passed in by whoever
    // draws the field, so one stored instant reads one way everywhere.
    const temporal = TEMPORAL_DISPLAY[meta.storageType as keyof typeof TEMPORAL_DISPLAY];
    if (temporal) {
      const moment = temporalMoment(meta.storageType, value);
      // A value that does not denote a moment is NOT silently blanked: it is shown as it is, so a
      // column holding something unexpected reads as the surprise it is rather than as empty.
      if (!moment) return String(value);
      return isRelative(meta) ? relativeMoment(moment, meta.storageType) : new Intl.DateTimeFormat(undefined, temporal).format(moment);
    }
    switch (meta.storageType) {
      case 'Boolean':
        return value ? 'Yes' : 'No';
      case 'Select':
        return this.optionLabel(String(value));
      case 'Geolocation': {
        // CAOS-620 — a place arrives as the named pair `{ latitude, longitude }`. Falling through
        // to the default would render it as `[object Object]`, so it is formatted here: latitude
        // first, the order a person reads and pastes into a map.
        // CAOS-642 — and each number labelled by its hemisphere, in the same words the kernel's
        // display block uses, so the record page and a list view never show one place two ways.
        const place = value as { latitude?: unknown; longitude?: unknown };
        if (typeof place?.latitude !== 'number' || typeof place?.longitude !== 'number') return '';
        return formatPlace({ latitude: place.latitude, longitude: place.longitude });
      }
      case 'File':
        // CAOS-596 — a File value is a LIST, and the default branch renders a list of objects as
        // `[object Object]` on every record page that places the field. That is the same defect
        // CAOS-620 fixed for Geolocation, still open for this kind because it had no editor and
        // so nothing was writing it. Adding the editor is what makes it reachable, so the read
        // side is fixed in the same change rather than left as the next surprise.
        return this.formatFileList(value);
      case 'JSON': {
        // CAOS-1674 — the same defect the File and Geolocation cases above call out: an object
        // reaching the default branch renders as `[object Object]` on every record page that places
        // the field. An address is the first JSON value anything writes, so the read side is fixed
        // in the change that makes it reachable rather than left as the next surprise.
        //
        // Only the `address` FORMAT is rendered. A JSON field with no format holds something
        // arbitrary and there is no words-for-it to produce — rendering a serialised blob into a
        // record page would be worse than the blank, and the kernel's display block returns null
        // for exactly the same reason.
        if (meta.format !== 'address') return '';
        if (value === null || typeof value !== 'object' || Array.isArray(value)) return '';
        return formatAddress(value as AddressValue);
      }
      case 'Number':
      case 'AutoNumber':
      case 'Formula':
      case 'Rollup':
      case 'Overridable':
        return this.formatNumericish(meta, value);
      default:
        return String(value);
    }
  }

  /**
   * The attached files, named. Tolerant of the shapes this value genuinely arrives in: a real
   * array, a JSON array serialised into a Text column (which is what the Report a Bug intake
   * writes today), a list of `{ name, url }` objects, or a single bare URL.
   *
   * A data URL has no name to show and is unreadable as text, so a list of them reads as a
   * count. Printing a 200KB base64 string into a record page is the other way this goes wrong.
   */
  private formatFileList(value: unknown): string {
    let list: unknown[];
    if (Array.isArray(value)) {
      list = value;
    } else if (typeof value === 'string') {
      const text = value.trim();
      if (text.startsWith('[')) {
        try {
          const parsed: unknown = JSON.parse(text);
          list = Array.isArray(parsed) ? parsed : [text];
        } catch {
          // Not JSON after all — a single value that happens to start with a bracket.
          list = [text];
        }
      } else {
        list = text === '' ? [] : [text];
      }
    } else {
      list = [value];
    }

    const names = list
      .map((entry) => {
        if (entry && typeof entry === 'object') {
          const named = entry as { name?: unknown; url?: unknown };
          if (typeof named.name === 'string' && named.name !== '') return named.name;
          if (typeof named.url === 'string') return this.fileNameFromUrl(named.url);
          return '';
        }
        if (typeof entry === 'string') return this.fileNameFromUrl(entry);
        return '';
      })
      .filter((n) => n !== '');

    if (names.length === 0) {
      // Present but unnameable — say how many rather than nothing, so a record with three
      // screenshots does not read as a record with none.
      const count = list.filter((e) => e !== null && e !== undefined && e !== '').length;
      if (count === 0) return '';
      return count === 1 ? '1 file' : `${count} files`;
    }
    return names.join(', ');
  }

  /** The readable tail of a URL, or '' for a data URL, which has no name in it. */
  private fileNameFromUrl(url: string): string {
    if (url === '') return '';
    if (url.startsWith('data:')) return '';
    const withoutQuery = url.split(/[?#]/)[0] ?? '';
    const tail = withoutQuery.split('/').pop() ?? '';
    try {
      return decodeURIComponent(tail);
    } catch {
      // A malformed percent-escape is not a reason to render nothing.
      return tail;
    }
  }

  private formatNumericish(meta: CaosFieldMeta, value: unknown): string {
    const fmt = meta.format ?? this.formatFromReturnType(meta);
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) return String(value);
    if (fmt === 'currency') {
      return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
    }
    if (fmt === 'percent') {
      return `${num.toLocaleString(undefined, { maximumFractionDigits: meta.scale ?? 2 })}%`;
    }
    if (typeof meta.scale === 'number') {
      return num.toLocaleString(undefined, { minimumFractionDigits: meta.scale, maximumFractionDigits: meta.scale });
    }
    return String(value);
  }

  /** A computed field (Formula/Overridable) formats by its declared return type. */
  private formatFromReturnType(meta: CaosFieldMeta): FieldFormat | undefined {
    if (meta.formulaReturnType === 'currency') return 'currency';
    if (meta.formulaReturnType === 'percent') return 'percent';
    return undefined;
  }

  private maskedDisplay(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'masked';
    span.setAttribute('part', 'masked');
    span.setAttribute('aria-label', 'Hidden — you do not have access to this value');
    const lock = document.createElement('span');
    lock.className = 'lock';
    lock.setAttribute('aria-hidden', 'true');
    lock.append(makeIcon('lock'));
    span.append(lock, document.createTextNode('••••••'));
    return span;
  }

  // ── edit-mode dispatch (EXHAUSTIVE over StorageType) ────────────────────────

  private editControl(storageType: StorageType, meta: CaosFieldMeta): HTMLElement {
    switch (storageType) {
      case 'Text':
        // CAOS-600 — three formats of one kind, three controls, one field: formatted text, several
        // plain lines, or one. The `long` case is the one this ticket adds, and it is an attribute
        // on the same control rather than a second component beside it.
        return isRich(meta) ? this.richTextControl(meta) : this.textControl(meta);
      case 'Number':
        return this.numberControl(meta);
      case 'Boolean':
        return this.checkboxControl(meta);
      case 'Date':
        return this.dateControl(meta, 'date');
      case 'DateTime':
        // CAOS-600 — edited as a day AND a time. It used to edit by its date part alone, which was
        // recorded as a documented gap but was worse than a gap: saving an unchanged DateTime wrote
        // midnight over whatever time it held. The temporal presentations of the date field close it.
        return this.dateControl(meta, 'datetime');
      case 'Select':
        return this.selectControl(meta);
      case 'MultiSelect':
        return this.multiSelectControl(meta);
      case 'Reference':
        return this.referenceControl(meta, 'edit');
      case 'Overridable':
        // The editable part of an Overridable is its nullable override column, of the
        // computed side's underlying LEAF type — resolve it and render that leaf's control.
        return this.editControl(overridableLeaf(meta.formulaReturnType) ?? 'Text', meta);
      case 'Formula':
      case 'Rollup':
      case 'AutoNumber':
        // Computed / engine-assigned — never client-writable (kernel READ_ONLY_STORAGE_TYPES);
        // edit mode shows the read-only value.
        return this.readOnlyControl(meta);
      case 'Time':
        // CAOS-600 — the editor the incumbent survey names as the real gap behind its formatted-time
        // display. The kernel has stored a zoneless time of day since the temporal set was
        // completed and nothing could edit one; this is the date field asking the platform for a
        // time affordance instead of a calendar, which is what the value IS.
        return this.dateControl(meta, 'time');
      case 'ZonedDateTime':
        // Still read-only, and deliberately. A wall-clock time in a NAMED ZONE is two stored parts,
        // and an editor for it has to let a person change the zone as well as the clock — which is a
        // control this library does not have and a question this ticket does not answer. A
        // `datetime-local` box here would silently drop the zone half of the value, which is the
        // defect the Time and DateTime cases above just stopped doing.
        return this.readOnlyControl(meta);
      case 'Geolocation':
        // CAOS-849 — one control for the one value. `caos-location-field` holds both coordinates and
        // commits them as a pair, which is why this was never a pair of number boxes: two
        // independently-typed boxes are exactly the separately-writable subfields the storage kind
        // exists to prevent.
        return this.locationControl(meta);
      case 'File':
        // CAOS-596 — the editor now exists, so this kind stops falling through to read-only.
        // Its transport is the field's own concern: `caos-file-field` defaults to the data-URL
        // reader the intake path already uses, and whoever mounts the renderer replaces that one
        // function when a durable store and its route exist (CAOS-508).
        return this.fileControl(meta);
      case 'JSON':
        // CAOS-1674 — the `address` format now HAS an editor, so a JSON field declaring it stops
        // falling through to read-only. A JSON field with no format still does: its value is
        // arbitrary, and a structured JSON editor is a later field-library primitive. That is the
        // format axis doing exactly what it is for — the storage kind is unchanged and what changed
        // is what the value says it is.
        return meta.format === 'address' ? this.addressControl(meta) : this.readOnlyControl(meta);
      case 'Localizable':
        // No dedicated editor primitive in this slice — render the read-only value; a locale-map
        // editor is a later field-library primitive.
        return this.readOnlyControl(meta);
      default:
        // If the kernel adds a StorageType and no case above handles it, `storageType`
        // is NOT `never` here and this fails to compile — the exhaustiveness guard.
        return assertNever(storageType);
    }
  }

  private textControl(meta: CaosFieldMeta): HTMLElement {
    const el = document.createElement('caos-text-field');
    el.setAttribute('part', 'control');
    // CAOS-600 / CAOS-452 — the Text kind's declared format, drawn by the same field: `long` as
    // several lines, and `email`, `phone` and `url` as the input each asks for. A field in no
    // particular format sets no format and edits in one plain line exactly as before.
    //
    // `long` no longer needs `multiline` set beside it — the component derives that from the
    // format, so the two cannot disagree about how many lines this field has.
    const format = textFieldFormat(meta.format);
    if (format) el.setAttribute('format', format);
    // CAOS-452 — the declared limit, enforced by the control rather than by a truncation somewhere
    // below it. A field with no declared length is unbounded here exactly as it was.
    if (typeof meta.length === 'number') el.setAttribute('maxlength', String(meta.length));

    if (labelText(meta.label)) el.setAttribute('label', labelText(meta.label));
    if (meta.required) el.setAttribute('required', '');
    if (this._value != null) el.setAttribute('value', String(this._value));
    el.addEventListener('change', () => this.dispatchChange((el as unknown as { value: string }).value));
    return el;
  }

  private numberControl(meta: CaosFieldMeta): HTMLElement {
    const el = document.createElement('caos-number-field');
    el.setAttribute('part', 'control');
    if (labelText(meta.label)) el.setAttribute('label', labelText(meta.label));
    if (meta.required) el.setAttribute('required', '');
    if (typeof meta.scale === 'number') el.setAttribute('step', String(1 / Math.pow(10, meta.scale)));
    if (this._value != null) el.setAttribute('value', String(this._value));
    el.addEventListener('change', () => this.dispatchChange((el as unknown as { value: string }).value));
    return el;
  }

  private checkboxControl(meta: CaosFieldMeta): HTMLElement {
    const el = document.createElement('caos-checkbox-field');
    el.setAttribute('part', 'control');
    if (meta.required) el.setAttribute('required', '');
    if (this._value) el.setAttribute('checked', '');
    if (labelText(meta.label)) el.append(document.createTextNode(labelText(meta.label)));
    el.addEventListener('change', () => this.dispatchChange((el as unknown as { checked: boolean }).checked));
    return el;
  }

  /**
   * CAOS-600 — one control for the three temporal kinds, told which affordance to ask for. The
   * caller passes the presentation rather than the field deciding for itself, because the kind is
   * what decides and the kind is already being switched on at the one call site above.
   */
  private dateControl(meta: CaosFieldMeta, format: CaosDateFieldFormat): HTMLElement {
    const el = document.createElement('caos-date-field');
    el.setAttribute('part', 'control');
    // `date` is the element's default and adds no attribute, so a plain Date field renders the
    // exact markup it did before this existed.
    if (format !== 'date') el.setAttribute('format', format);
    if (labelText(meta.label)) el.setAttribute('label', labelText(meta.label));
    if (meta.required) el.setAttribute('required', '');
    if (this._value != null) el.setAttribute('value', String(this._value));
    // All three temporal presentations are segmented native inputs, so this control's `change` is a
    // per-segment event. Declared on the element itself rather than inferred by the caller — see
    // {@link SegmentedEditor}.
    (el as SegmentedEditor).firesChangePerSegment = true;
    el.addEventListener('change', () => {
      // CAOS-1678 — as the INLINE editor this is silent, and `inlineValue` commits once on blur or
      // Enter. Dispatching per segment would write and toast twice for one edit, and would leave
      // Escape unable to cancel, because the hour had already been saved by the time it was pressed.
      // In a form nothing changes: there is no wrapper to own the commit, and a form saves on submit.
      if (el.hasAttribute('inline')) return;
      this.dispatchChange((el as unknown as { value: string }).value);
    });
    return el;
  }

  private selectControl(meta: CaosFieldMeta): HTMLElement {
    const el = document.createElement('caos-picklist-field') as HTMLElement & { options: CaosPicklistOption[] };
    el.setAttribute('part', 'control');
    if (labelText(meta.label)) el.setAttribute('label', labelText(meta.label));
    if (meta.required) el.setAttribute('required', '');
    el.setAttribute('placeholder', 'Choose…');
    // CAOS-640 — a declared cards presentation is forwarded as the attribute the field reads. A set
    // that declares nothing stays the dropdown it has always been, so this adds no attribute at all.
    // CAOS-600 — and the same for a declared radio presentation. Which one a field uses is declared
    // on the VALUE SET, beside the values themselves, so the list and the way it is drawn are one
    // declaration and cannot drift apart.
    if (this._optionsPresentation !== 'dropdown') el.setAttribute('presentation', this._optionsPresentation);
    el.options = this._options;
    if (this._value != null) el.setAttribute('value', String(this._value));
    el.addEventListener('change', () => this.dispatchChange((el as unknown as { value: string }).value));
    return el;
  }

  /**
   * MultiSelect edits as the picklist field's several-values presentation (CAOS-597): the same declared
   * options, one checkbox each, drawn by the field rather than assembled here.
   */
  private multiSelectControl(meta: CaosFieldMeta): HTMLElement {
    const el = document.createElement('caos-picklist-field');
    el.setAttribute('part', 'control');
    el.setAttribute('multiple', '');
    if (labelText(meta.label)) el.setAttribute('label', labelText(meta.label));
    if (meta.required) el.setAttribute('required', '');
    el.options = this._options;
    el.values = (Array.isArray(this._value) ? this._value : []).map(String);
    el.addEventListener('change', () => this.dispatchChange(el.values));
    return el;
  }

  /** CAOS-597 — a MultiSelect value read as one tag per value, in a pill container named by the field. */
  private pillsFor(values: readonly unknown[], meta: CaosFieldMeta): HTMLElement {
    const pills = document.createElement('caos-pill-container');
    pills.className = 'tags';
    if (labelText(meta.label)) pills.setAttribute('label', labelText(meta.label));
    for (const v of values) {
      const tag = document.createElement('caos-tag');
      tag.textContent = this.optionLabel(String(v));
      pills.append(tag);
    }
    return pills;
  }

  private referenceControl(meta: CaosFieldMeta, mode: CaosFieldMode): HTMLElement {
    const el = document.createElement('caos-lookup-field') as HTMLElement & { search: CaosLookupSearch | null };
    el.setAttribute('part', 'control');
    el.setAttribute('mode', mode);
    if (labelText(meta.label)) el.setAttribute('label', labelText(meta.label));
    if (meta.required) el.setAttribute('required', '');
    if (this._value != null) el.setAttribute('value', String(this._value));
    const display = this._refDisplay ?? (this._value != null ? String(this._value) : '');
    if (display) el.setAttribute('display', display);
    if (this._refHref) el.setAttribute('href', this._refHref);
    if (this._search) el.search = this._search;
    el.addEventListener('caos-lookup-change', (e) => {
      const detail = (e as CustomEvent<{ value: string }>).detail;
      this.dispatchChange(detail?.value ?? '');
    });
    el.addEventListener('caos-lookup-clear', () => this.dispatchChange(''));
    return el;
  }

  /**
   * CAOS-596 — the File editor. The stored value is a list, and it arrives in more than one
   * shape depending on who wrote it (a JSON array in a Text column, a real array, a bare URL),
   * so it is normalised on the way in the same way {@link formatFileList} normalises it for
   * reading. The control reports its accepted files back as the same list of URLs it was given,
   * which keeps a round trip through the editor value-preserving.
   */
  private fileControl(meta: CaosFieldMeta): HTMLElement {
    const el = document.createElement('caos-file-field');
    el.setAttribute('part', 'control');
    el.setAttribute('multiple', '');
    if (labelText(meta.label)) el.setAttribute('label', labelText(meta.label));
    if (meta.required) el.setAttribute('required', '');
    el.addEventListener('caos-file-change', (e) => {
      const detail = (e as CustomEvent<{ value: readonly { url: string }[] }>).detail;
      this.dispatchChange((detail?.value ?? []).map((v) => v.url));
    });
    return el;
  }

  /**
   * CAOS-849 — the Geolocation editor. The stored value is handed over as the coordinates it
   * carries. A half-set or non-numeric value (one that never went through the save) opens with
   * whatever it does carry and commits nothing until the pair stands — the editor's side of the
   * "never half a place" rule `formatRead` keeps when reading.
   */
  private locationControl(meta: CaosFieldMeta): HTMLElement {
    const el = document.createElement('caos-location-field');
    el.setAttribute('part', 'control');
    if (labelText(meta.label)) el.setAttribute('label', labelText(meta.label));
    if (meta.required) el.setAttribute('required', '');
    const point = this._value;
    if (point !== null && typeof point === 'object') {
      for (const axis of ['latitude', 'longitude'] as const) {
        const v = (point as Record<string, unknown>)[axis];
        if (typeof v === 'number' || typeof v === 'string') el.setAttribute(axis, String(v));
      }
    }
    el.addEventListener('change', () => this.dispatchChange(el.value));
    return el;
  }

  /**
   * CAOS-1674 — the address format's editor: one box per part, committing one value.
   *
   * Built here rather than as a `caos-address-field` element, and that is the architecture's own
   * distinction rather than a shortcut. `meta/field-type.ts` separates a value that earns a STORAGE
   * KIND — a point on the earth, which the database must understand as a whole — from one that is a
   * COMPOSED FIELD TYPE over the JSON primitive, which an address is. Geolocation took the first
   * road and has its own component, `caos-location-field`. An address takes the second, so it gets
   * what the other composed types get: a format on this renderer, the same answer `email`, `phone`
   * and `url` received. It adds no catalogue entry, which is what the incumbent survey asked for
   * when it put its address rows in the improved bucket.
   *
   * It commits the WHOLE object on any part changing, never a part at a time. Separately-writable
   * subfields are exactly what a compound-field concept would be, and the platform deliberately has
   * none — so a half-committed address must not be representable.
   */
  private addressControl(meta: CaosFieldMeta): HTMLElement {
    const group = document.createElement('div');
    group.className = 'address';
    group.setAttribute('part', 'control');
    // A group of inputs standing for ONE field needs a group name, or a screen reader reads five
    // unrelated boxes. `role=group` + the field's own label is what names it.
    group.setAttribute('role', 'group');
    const groupLabel = labelText(meta.label);
    if (groupLabel) group.setAttribute('aria-label', groupLabel);

    const current = (
      this._value !== null && typeof this._value === 'object' && !Array.isArray(this._value) ? this._value : {}
    ) as Record<string, unknown>;

    const boxes = new Map<AddressPart, HTMLElement & { value?: unknown }>();

    /** Assemble the whole value from every box, and commit it. */
    const commit = (): void => {
      const next: Record<string, string> = {};
      for (const part of ADDRESS_PARTS) {
        const raw = boxes.get(part)?.value;
        const text = typeof raw === 'string' ? raw.trim() : '';
        if (text !== '') next[part] = text;
      }
      // Nothing filled in commits NULL, not `{}`. "No address" is null — the save path refuses an
      // empty object precisely so the two never both mean the same thing, and the control must not
      // be the thing that produces the value the kernel then rejects.
      this.dispatchChange(Object.keys(next).length === 0 ? null : next);
    };

    for (const part of ADDRESS_PARTS) {
      const box = document.createElement('caos-text-field') as HTMLElement & { value?: unknown };
      box.setAttribute('part', 'address-part');
      box.setAttribute('data-part', part);
      box.setAttribute('label', ADDRESS_PART_LABELS[part]);
      const existing = current[part];
      if (typeof existing === 'string' && existing !== '') box.setAttribute('value', existing);
      // `required` belongs to the FIELD, not to any one part: which parts a correct address fills
      // in differs by country, and the address validator is what says a part is needed. Marking
      // every box required would demand a state in the many countries that have none.
      box.addEventListener('change', commit);
      boxes.set(part, box);
      group.append(box);
    }
    return group;
  }

  /** CAOS-1674 — an address on its own lines, the way an address is written on an envelope. */
  private addressReadDisplay(): HTMLElement {
    const value = this._value;
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return this.blankValue();
    const lines = formatAddressLines(value as AddressValue);
    if (lines.length === 0) return this.blankValue();
    const block = document.createElement('div');
    block.className = 'value address';
    block.setAttribute('part', 'value');
    for (const line of lines) {
      const row = document.createElement('div');
      row.className = 'address-line';
      row.textContent = line;
      block.append(row);
    }
    return block;
  }

  /** CAOS-595 — a rich value, sanitised, with plain-text web and email addresses shown as links. */
  private richReadDisplay(): HTMLElement {
    if (isRichTextEmpty(this._value)) return this.blankValue();
    const block = document.createElement('div');
    block.className = 'value rich';
    block.setAttribute('part', 'value');
    const content = richTextFragment(this._value);
    linkifyRichText(content);
    block.append(content);
    return block;
  }

  /** CAOS-595 — the rich format's editor: the platform's toolbar, then the buttons packages contributed. */
  private richTextControl(meta: CaosFieldMeta): HTMLElement {
    return richTextEditor({
      ...(labelText(meta.label) ? { label: labelText(meta.label) } : {}),
      ...(meta.required ? { required: true } : {}),
      value: this._value,
      contributions: this._richTextToolbar,
      onCommit: (value) => this.dispatchChange(value),
    });
  }

  /** A locked, read-only rendering used for computed / unsupported-editor kinds in edit mode. */
  private readOnlyControl(meta: CaosFieldMeta): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'readonly';
    wrap.setAttribute('part', 'control');
    const lock = document.createElement('span');
    lock.className = 'lock';
    lock.setAttribute('aria-hidden', 'true');
    lock.append(makeIcon('lock'));
    wrap.append(lock, this.readDisplay(meta));
    return wrap;
  }

  private dispatchChange(value: unknown): void {
    this._value = value;
    this.dispatchEvent(new CustomEvent('caos-field-change', { bubbles: true, composed: true, detail: { value } }));
  }
}

/**
 * The underlying leaf STORAGE kind an Overridable field's override column takes, from its
 * computed side's declared return type. Mirrors the kernel's `overridableUnderlyingStorage`
 * (`docs/.../CAOS_METADATA_MODEL.md` §G6) — kept as a local, `StorageType`-typed switch so
 * this leaf library stays runtime-free of the kernel while the mapping is compile-checked.
 * `time` (and any unknown return type) has no in-scope override leaf → `null`.
 */
function overridableLeaf(returnType: CaosFieldMeta['formulaReturnType']): StorageType | null {
  switch (returnType) {
    case 'number':
    case 'currency':
    case 'percent':
      return 'Number';
    case 'text':
      return 'Text';
    case 'boolean':
      return 'Boolean';
    case 'date':
      return 'Date';
    case 'datetime':
      return 'DateTime';
    default:
      return null;
  }
}

define('caos-field-renderer', CaosFieldRenderer);

declare global {
  interface HTMLElementTagNameMap {
    'caos-field-renderer': CaosFieldRenderer;
  }
}
